import React, { useContext } from 'react';
import { GeaoContext } from '../../../GeaoContextProvider';
import { ClientConfigurationContext } from '../../../../../context/ClientConfigurationContextProvider';
import { useFormContext } from 'react-hook-form';
import DateOfBirth from '../../../../gds/DateOfBirth';
import InputBox from '../../../../gds/InputBox';

/**
 * Personal Information Section Component
 * 
 * A form section that displays the agent's full name and collects personal information
 * including date of birth and Social Security Number. The component retrieves name
 * information from the GEAO context and displays it as a header.
 * 
 * @component
 * @returns {JSX.Element} The rendered personal information form section
 * 
 * @example
 * // Usage within GeaoContextProvider and react-hook-form FormProvider contexts
 * <GeaoContextProvider>
 *   <FormProvider methods={methods}>
 *     <PersonalInfoSection />
 *   </FormProvider>
 * </GeaoContextProvider>
 * 
 * @requires GeaoContext - Must be used within a GeaoContextProvider
 * @requires react-hook-form - Must be used within a FormProvider context
 * 
 * Form fields managed:
 * - dateOfBirth: Date - Agent's date of birth
 * - ssn: String - Agent's Social Security Number
 * - ssnConfirm: String - Confirmation of Social Security Number
 * 
 * Context dependencies:
 * - agentInterview.questionAnswers.FirstName.answer: String - Agent's first name
 * - agentInterview.questionAnswers.MiddleName.answer: String - Agent's middle name (optional)
 * - agentInterview.questionAnswers.LastName.answer: String - Agent's last name
 * - agentInterview.questionAnswers.Suffix.answer: String - Agent's name suffix (optional)
 */
export default function PersonalInfoSection() {
    const { agentInterview } = useContext(GeaoContext);
    const clientConfiguration = useContext(ClientConfigurationContext);
    const { control } = useFormContext();

    const isOffshoreResident = agentInterview?.questionAnswers?.OffshoreResident?.answer === "true";
    const enableOffshore = clientConfiguration?.enableOffshore === true;
    const hideSSN = enableOffshore && isOffshoreResident;

    /**
     * Constructs the agent's full name from interview question answers
     * Combines first name, middle name, last name, and suffix (if available)
     * Filters out empty/null values and joins with spaces
     * @type {string} The agent's formatted full name
     */
    const fullName = [
        agentInterview?.questionAnswers?.FirstName?.answer,
        agentInterview?.questionAnswers?.MiddleName?.answer,
        agentInterview?.questionAnswers?.LastName?.answer,
        agentInterview?.questionAnswers?.Suffix?.answer
    ].filter(Boolean).join(' ');

    return (
        <>
            <h2 className="margin-bottom">{fullName}</h2>
            <div className="tooltip-label-container" style={{ display: 'flex', alignItems: 'center' }}>
                <h3 className="margin-top margin-bottom">Personal Info</h3>
                <gds-tooltip data-onboarding-control-id="shared:PersonalInformation:PersonalInfoSection:PrivacyInformation">At GEICO, we understand the importance of your privacy and security. The information you
                    provide will be kept confidential and secure. It will not be sold or redistributed. To learn
                    more about how we protect your personal information, please review our privacy &amp; security
                    policy: <a data-onboarding-link-id="shared:PersonalInformation:PersonalInfoSection:PrivacyPolicy" href="https://media.geico.com/legal/privacy_policy.htm" target="_blank"
                        className=""> Privacy Policy</a></gds-tooltip>
            </div>

            <div className="flex-row">
                <DateOfBirth
                    control={control}
                    name="dateOfBirth"
                    label={<>
                        Date of Birth{/*
                        */}<span className='required'>*</span>
                    </>}
                    showTooltip={false}
                />

                {!hideSSN && (
                    <>
                        <div style={{ marginRight: "1rem" }}>
                            <InputBox
                                control={control}
                                name="ssn"
                                label={<>
                                    SSN{/*
                                */}<span className='required'>*</span>
                                </>}
                                maskType="ssn"
                                includeVisibilityToggle={true}
                            />
                        </div>

                        <div>
                            <InputBox
                                control={control}
                                name="ssnConfirm"
                                label={<>
                                    Confirm SSN{/*
                                */}<span className='required'>*</span>
                                </>}
                                maskType="ssn"
                                includeVisibilityToggle={true}
                            />
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
